import { useTranslation } from "react-i18next";
import {
  ACTIVITY_DETAIL_DISPLAY_RULE_GROUPS,
  evaluateActivityDetailDisplayRules,
} from "../utils/activityDetailDisplayRules";

const VisibilityMark = ({ visible }) =>
  visible ? (
    <span
      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#E8F8F0] text-[#1F7A55]"
      aria-label="visible"
      title="visible"
    >
      <i className="fas fa-check text-sm" aria-hidden />
    </span>
  ) : (
    <span
      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#FFF0F0] text-[#C24141]"
      aria-label="hidden"
      title="hidden"
    >
      <i className="fas fa-times text-sm" aria-hidden />
    </span>
  );

/**
 * Admin-only tab: documents every activity-detail visibility rule and
 * whether it currently applies (section shown) on this collection.
 */
const ActivityDetailDisplayRulesTab = ({
  historyCycle,
  data,
  dataSortedByAnomalies,
  isSuperContainer,
}) => {
  const { t } = useTranslation();
  const rules = evaluateActivityDetailDisplayRules({
    historyCycle,
    data,
    dataSortedByAnomalies,
    isSuperContainer,
  });
  const readingCount = Array.isArray(historyCycle) ? historyCycle.length : 0;

  return (
    <div className="flex flex-col gap-6 w-full">
      <section className="flex flex-col gap-2">
        <div className="char-section-header char-section-header--general-info !mb-0 min-w-0">
          <h2 className="char-section-header-title !mb-0">
            {t("activityDetailDisplayRules.title")}
          </h2>
        </div>
        <p className="m-0 text-sm text-[#6B7280] max-w-3xl">
          {t("activityDetailDisplayRules.intro")}
        </p>
        <p className="m-0 text-sm text-[#222222]">
          {t("activityDetailDisplayRules.context", {
            count: readingCount,
            timeline:
              readingCount > 1
                ? t("activityDetailDisplayRules.timelineMulti")
                : t("activityDetailDisplayRules.timelineSingle"),
          })}
        </p>
      </section>

      {ACTIVITY_DETAIL_DISPLAY_RULE_GROUPS.map((group) => {
        const groupRules = rules.filter((rule) => rule.group === group);
        if (groupRules.length === 0) return null;

        return (
          <section key={group} className="flex flex-col min-w-0 gap-3">
            <div className="char-section-header char-section-header--general-info !mb-0 min-w-0">
              <h3 className="char-section-header-title !mb-0">
                {t(`activityDetailDisplayRules.group.${group}`)}
              </h3>
            </div>
            <article className="bg-white border border-gray-300 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse text-left">
                  <thead>
                    <tr className="bg-[#F8F8F8] border-b border-gray-200">
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                        {t("activityDetailDisplayRules.column.section")}
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                        {t("activityDetailDisplayRules.column.rule")}
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280] text-center w-28">
                        {t("activityDetailDisplayRules.column.current")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupRules.map((rule) => (
                      <tr
                        key={rule.id}
                        className="border-b border-gray-100 last:border-b-0"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-[#222222] align-top">
                          {t(`activityDetailDisplayRules.${rule.id}.section`)}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#4B5563] align-top">
                          {t(`activityDetailDisplayRules.${rule.id}.rule`)}
                        </td>
                        <td className="px-4 py-3 text-center align-middle">
                          <div className="flex justify-center">
                            <VisibilityMark visible={rule.visible} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          </section>
        );
      })}
    </div>
  );
};

export default ActivityDetailDisplayRulesTab;
